import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { WEB_COMPONENT_ASSIGN_SECURITY_SERVERS } from '../../../../../webcomponents.constants';
import { ConsoleRequest, DataProviderType } from '../../../../../utils/request';
import {
  AssignedSecurityServerItem,
  FutureAssignedSecurityServerTableItem,
  GetAssignedSecurityServersForEntitiesResponse,
  GetShareableSecurityServersResponse,
  SecurityServer,
} from './types';
import { styles } from './view/styles';
import { DataTableLoaderLayout } from '../../../../../ui/gzDataTable/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { DATA_TABLE_DEFAULT_LOADER_LAYOUT } from '../../../../../ui/gzDataTable/types/constants';
import { FontSizes, LabelWidthValues, MenuPosition } from '../../../../../types/common/types';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType, FontSize } from '../../../../../ui/gzIconButton/types';
import { GzEnumFilterWebComponent } from '../../../../../ui/gzEnumFilter';
import { GzIconButtonWebComponent } from '../../../../../ui/gzIconButton';
import { GzDataTableWebComponent } from '../../../../../ui/gzDataTable';
import { AssignSecurityServersHelper } from './helpers/assign.security.servers.helper';
import { elementClassListAdd, elementClassListRemove } from '../../../../../utils/htmlElement.utils';
import { ImageState } from '../../../../../ui/gzImageRepo/types';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';

@customElement(WEB_COMPONENT_ASSIGN_SECURITY_SERVERS)
export class GzAssignSecurityServersWebComponent extends LitElement {
  @property({ attribute: false, reflect: true, type: Array })
  public targetIds: Array<string>;

  @state()
  private alreadyAssignedSecurityServers: Array<AssignedSecurityServerItem> = [];

  @state()
  private isErrorState = false;

  @query('gz-enum-filter#security-servers-filter')
  private securityServersFilter: GzEnumFilterWebComponent;

  @query('gz-icon-button#add-security-server')
  private addIconButton: GzIconButtonWebComponent;

  @query('gz-data-table#future-assignation-table')
  private assignationDataTable: GzDataTableWebComponent<FutureAssignedSecurityServerTableItem>;

  private securityServers: Array<SecurityServer> | undefined = undefined;
  private loaderLayout: DataTableLoaderLayout = {
    rows: DATA_TABLE_DEFAULT_LOADER_LAYOUT.rows,
    columns: 2,
  };

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.getSecurityServersData()
      .then()
      .catch(() => {
        this.isErrorState = true;
        this.dispatchEvent(
          new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
            bubbles: true,
            composed: true,
            detail: { value: true },
          })
        );
      });
  }

  public getFutureSecurityServerEndpointIds(): Array<string> {
    if (!this.securityServers || this.securityServers.length === 0) {
      return [];
    }

    const futureSecurityServerIds = this.assignationDataTable.getFormValue().map(item => item.id);
    const futureSecurityServersData = this.securityServers.filter(item => futureSecurityServerIds.includes(item._id));
    return futureSecurityServersData.map(item => item.specifics.endpointId);
  }

  private renderErrorState(): TemplateResult {
    return html`
      <div id="error-state-container">
        <gz-image-repo state="${ImageState.ERROR}">
          <gz-text fontWeight="400" fontSize="14"
            >${translateFn('Cannot assign Security Servers. Check entity availability.')}</gz-text
          >
        </gz-image-repo>
      </div>
    `;
  }

  protected render(): TemplateResult {
    return html` ${this.isErrorState ? this.renderErrorState() : this.renderSuccessState()} `;
  }

  private renderSuccessState(): TemplateResult {
    return html`
      <div id="success-state-container">
        ${this.renderAlreadyAssignedServersTable()} ${this.renderInfoTextAndWarning()} ${this.renderEnumFilter()}
        ${this.renderFutureSecurityServersTable()}
      </div>
    `;
  }

  private renderFutureSecurityServersTable(): TemplateResult {
    return html`
      <div class="pl-5">
        <gz-data-table
          class="hidden"
          id="future-assignation-table"
          .columns="${AssignSecurityServersHelper.getSecurityServersToBeAssignedColumns()}"
          .strings="${AssignSecurityServersHelper.getStringsForDataTable()}"
          .items="${[]}"
          allowRowDeletion
          @row-deleted="${this.handleDeletionOfSecurityServer}"
        >
        </gz-data-table>
      </div>
    `;
  }

  private renderEnumFilter(): TemplateResult {
    return html`
      <div class="d-flex pl-5 pb-2" id="filter-and-add-button-container">
        <gz-enum-filter
          id="security-servers-filter"
          .canSelectAll="${true}"
          allItemsLabel="${translateFn('All')}"
          confirmActionButtonString="${translateFn('Apply')}"
          .filterOptions="${undefined}"
          .allowSelectNone="${true}"
          .labelWidth="${LabelWidthValues.FULL}"
          placeholder="${translateFn('Select Security Servers')}"
          nosearchresultsstring="${translateFn('No Security Servers available.')}"
          .isLoading="${true}"
          menuPosition="${MenuPosition.RIGHT}"
          @selectionChange="${this.handleSecurityServersSelectionChanged}"
        >
        </gz-enum-filter>
        <gz-icon-button
          class="pl-2"
          id="add-security-server"
          icon="ds-icon-add"
          .disabled="${this.getDisabledStateOfAddButton()}"
          buttonType="${ButtonType.SQUARE}"
          size="${ButtonSize.SIZE_32}"
          buttonFill="${ButtonFill.PRIMARY}"
          buttonColor="${ButtonColor.WHITE}"
          fontSize="${FontSize.ICON_SIZE_24}"
          @click="${this.handleAddSecurityServers}"
        >
        </gz-icon-button>
      </div>
    `;
  }

  private renderInfoTextAndWarning(): TemplateResult {
    return html`
      <div class="pt-4">
        <gz-text class="pl-5" fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Assign as many Security Servers as needed. Select and add them from the list below.')}
        </gz-text>
        <gz-info-box class="pl-5" severity="${InfoBoxSeverity.WARNING}">
          ${translateFn(
            'This action will overwrite previous assignments. If no Security Server is selected, all assignments will be removed.'
          )}
        </gz-info-box>
      </div>
    `;
  }

  private renderAlreadyAssignedServersTable(): TemplateResult {
    return html`
      <div class="pl-5">
        <gz-data-table
          .columns="${AssignSecurityServersHelper.getAlreadyAssignedSecurityServersColumns()}"
          .strings="${AssignSecurityServersHelper.getStringsForDataTable()}"
          .items="${this.alreadyAssignedSecurityServers}"
          .loading="${this.alreadyAssignedSecurityServers.length === 0}"
          .loaderLayout="${this.loaderLayout}"
        >
        </gz-data-table>
      </div>
    `;
  }

  private handleAddSecurityServers() {
    if (!this.securityServers || this.securityServers.length === 0) {
      console.error('No security servers data.');
      return;
    }

    const newAddedSecurityServerIds = this.getSelectedSecurityServerIdsFromFilter();
    const selectedSecurityServerData = this.securityServers.filter(data =>
      newAddedSecurityServerIds.includes(data._id)
    );

    const currentTableValues = this.assignationDataTable.getFormValue();
    this.assignationDataTable.setFormValue(
      currentTableValues.concat(
        selectedSecurityServerData.map(item => {
          return {
            id: item._id as string,
            name: item.name + ' (' + item.specifics.ip + ')',
          };
        })
      )
    );

    this.securityServersFilter.filterOptions = this.securityServersFilter.filterOptions.filter(item => !item.selected);
    this.addIconButton.disabled = true;

    elementClassListRemove(this.assignationDataTable, 'hidden');
  }

  private handleDeletionOfSecurityServer(event: CustomEvent) {
    if (!this.securityServers || this.securityServers.length === 0) {
      console.error('No security servers data.');
      return;
    }

    const deletedSecurityServerId = event.detail?.item?.id as string;
    if (!deletedSecurityServerId) {
      console.error('Could not get id of deleted security server.');
      return;
    }

    const securityServer = this.securityServers.find(item => item._id === deletedSecurityServerId);
    if (!securityServer) {
      console.error('Could not find deleted security servers from current data.');
      return;
    }

    this.securityServersFilter.filterOptions = this.securityServersFilter.filterOptions.concat([
      AssignSecurityServersHelper.transformSecurityServerToEnumListItem(securityServer),
    ]);
    if (this.securityServersFilter.filterOptions.length === this.securityServers.length) {
      elementClassListAdd(this.assignationDataTable, 'hidden');
    }
  }

  private handleSecurityServersSelectionChanged() {
    this.addIconButton.disabled = this.getDisabledStateOfAddButton();
  }

  private async getSecurityServersData() {
    const assignedSecurityServersPromise = ConsoleRequest.createRequest<GetAssignedSecurityServersForEntitiesResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getAssignedSecurityServersForEntities',
      payload: {
        targetsIds: this.targetIds,
      },
    });

    const securityServersPromise = ConsoleRequest.createRequest<GetShareableSecurityServersResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getShareableSecurityServers',
    });

    const [assignedSecurityServers, securityServers] = await Promise.all([
      assignedSecurityServersPromise,
      securityServersPromise,
    ]);

    this.securityServers = securityServers.records;
    this.securityServersFilter.filterOptions = securityServers.records.map(item =>
      AssignSecurityServersHelper.transformSecurityServerToEnumListItem(item)
    );
    this.securityServersFilter.isLoading = false;

    this.alreadyAssignedSecurityServers = AssignSecurityServersHelper.transformConsoleRequestToTableItems(
      assignedSecurityServers.records
    );
  }

  private getDisabledStateOfAddButton(): boolean {
    if (this.securityServers === undefined || this.securityServers.length === 0) {
      return true;
    }

    if (!this.securityServersFilter || !this.securityServersFilter.filterOptions) {
      return true;
    }

    return this.securityServersFilter.getCheckedValues().length === 0;
  }

  private getSelectedSecurityServerIdsFromFilter(): Array<string> {
    if (this.securityServersFilter.getCheckedValues().length === 0) {
      console.error('Could not add security servers. Missing checked data.');
      return [];
    }

    const selectedSecurityServerData = this.securityServersFilter.filterOptions.filter(item => item.selected);
    if (!selectedSecurityServerData || selectedSecurityServerData.length === 0) {
      elementClassListAdd(this.assignationDataTable, 'hidden');
      return [];
    }

    return selectedSecurityServerData.map(item => item.id) as Array<string>;
  }
}
